/**
 * 权限状态管理器
 * 负责统一管理权限状态的同步和响应
 */
import { ref, computed, watch } from 'vue';
import tokenService from './tokenService';
import { USER_ROLE_ADMIN, hasDevToolsAccess } from '@/constants/userRole';

// 全局权限状态
const permissionState = ref({
  isLoggedIn: false,
  userInfo: null,
  lastUpdateTime: Date.now()
});

// 权限变更监听器列表
const permissionListeners = new Set();

/**
 * 检查Token有效性并更新权限状态
 */
function updatePermissionState() {
  const hasValidToken = tokenService.hasAccessToken();
  const userInfo = hasValidToken ? tokenService.getCurrentUserInfo() : null;
  
  const newState = {
    isLoggedIn: hasValidToken && !!userInfo,
    userInfo: userInfo,
    lastUpdateTime: Date.now()
  };
  
  // 检查状态是否真的发生了变化
  const stateChanged = 
    permissionState.value.isLoggedIn !== newState.isLoggedIn ||
    permissionState.value.userInfo?.userId !== newState.userInfo?.userId ||
    permissionState.value.userInfo?.userRole !== newState.userInfo?.userRole;
  
  if (stateChanged) {
    console.log('权限状态发生变化:', {
      old: permissionState.value,
      new: newState
    });
    
    permissionState.value = newState;
    
    // 通知所有监听器
    notifyPermissionChange(newState);
  }
  
  return newState;
}

/**
 * 通知权限变更
 */
function notifyPermissionChange(newState) {
  permissionListeners.forEach(listener => {
    try {
      listener(newState);
    } catch (error) {
      console.error('权限变更监听器执行失败:', error);
    }
  });
}

/**
 * 添加权限变更监听器
 */
function addPermissionListener(listener) {
  permissionListeners.add(listener);
  
  // 返回移除监听器的函数
  return () => {
    permissionListeners.delete(listener);
  };
}

/**
 * 强制刷新权限状态
 */
function refreshPermissionState() {
  console.log('强制刷新权限状态');
  return updatePermissionState();
}

/**
 * 获取当前权限状态
 */
function getCurrentPermissionState() {
  return permissionState.value;
}

/**
 * 检查特定权限
 */
function checkPermission(permission) {
  const state = permissionState.value;
  
  switch (permission) {
    case 'admin':
      return state.isLoggedIn && state.userInfo?.userRole === USER_ROLE_ADMIN;
    case 'devtools':
      return state.isLoggedIn && hasDevToolsAccess(state.userInfo?.userRole);
    case 'login':
      return state.isLoggedIn;
    case 'guest':
      return !state.isLoggedIn;
    default:
      return true;
  }
}

/**
 * 响应式权限检查
 */
function usePermission(permission) {
  return computed(() => checkPermission(permission));
}

/**
 * 响应式用户信息
 */
function useUserInfo() {
  return computed(() => permissionState.value.userInfo);
}

/**
 * 响应式登录状态
 */
function useLoginState() {
  return computed(() => permissionState.value.isLoggedIn);
}

// 初始化权限状态
updatePermissionState();

// 定期检查Token有效性（每30秒）
setInterval(() => {
  updatePermissionState();
}, 30000);

// 监听localStorage变化（处理多标签页同步）
window.addEventListener('storage', (e) => {
  if (e.key === 'accessToken' || e.key === 'refreshToken') {
    console.log('检测到Token存储变化，更新权限状态');
    setTimeout(updatePermissionState, 100); // 延迟一点确保Token已更新
  }
});

export default {
  // 状态管理
  updatePermissionState,
  refreshPermissionState,
  getCurrentPermissionState,
  
  // 权限检查
  checkPermission,
  usePermission,
  
  // 响应式状态
  useUserInfo,
  useLoginState,
  
  // 监听器管理
  addPermissionListener,
  
  // 直接访问状态（用于非Vue组件）
  get isLoggedIn() {
    return permissionState.value.isLoggedIn;
  },
  
  get userInfo() {
    return permissionState.value.userInfo;
  },
  
  get isAdmin() {
    return permissionState.value.isLoggedIn && permissionState.value.userInfo?.userRole === USER_ROLE_ADMIN;
  },

  get hasDevTools() {
    return permissionState.value.isLoggedIn && hasDevToolsAccess(permissionState.value.userInfo?.userRole);
  }
};
